// <SoundToggle> — small speaker button, top-right of the slide.
//
// Themed via theme tokens. Stateless: the parent owns enabled/onToggle (from
// useThemeWorldAudio). Positioned absolute within <WorldStage>'s non-scrolling
// root, so it stays reachable however far the learner scrolls.
//
// The mark is a drawn SVG, not a 🔊/🔇 emoji: emoji render in the system's own
// full-colour font, which broke out of every world's palette (most visibly in
// the strictly two-colour neon-oracle terminal). `currentColor` keeps it in
// whatever tone the theme sets.

import type { Theme } from '../../components/course-builder/slides/slideTypes';

function SpeakerMark({ enabled }: { enabled: boolean }) {
  return (
    <svg width={15} height={15} viewBox="0 0 16 16" fill="none" aria-hidden focusable="false">
      <path
        d="M3 6h2.2L8.4 3.3v9.4L5.2 10H3z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth={1.1}
        strokeLinejoin="round"
      />
      {enabled ? (
        <>
          <path d="M10.6 5.9a3 3 0 0 1 0 4.2" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" />
          <path d="M12.4 4.2a5.4 5.4 0 0 1 0 7.6" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" />
        </>
      ) : (
        <path d="M10.8 6.2l3.4 3.6M14.2 6.2l-3.4 3.6" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" />
      )}
    </svg>
  );
}

export function SoundToggle({
  enabled,
  onToggle,
  theme,
}: {
  enabled: boolean;
  onToggle: () => void;
  theme: Theme;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const muted = theme.colors.textMuted;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={enabled ? 'Mute sound' : 'Enable sound'}
      aria-pressed={enabled}
      style={{
        position: 'absolute',
        top: 14,
        right: 16,
        // Above the atmosphere AND above the scrolling content it overlays.
        zIndex: 60,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: theme.fonts.body,
        lineHeight: 1,
        background: theme.colors.surface,
        color: enabled ? accent : muted,
        border: `1px solid ${enabled ? `${accent}80` : `${muted}55`}`,
        padding: '7px 9px',
        cursor: 'pointer',
        transition: 'color 0.3s, border-color 0.3s',
      }}
    >
      <SpeakerMark enabled={enabled} />
    </button>
  );
}
